import { type ReactNode } from "react";

import { SubscriptionTier } from "@repo/ui/pricing-hooks";
import { Button, Icon_ExclamationFilled16, InfoBar, Modal } from "@shapr3d/frontend-lib";

import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import {
  ElementClickAction,
  ElementClickLocation,
  ModalClosedAction,
  ModalShownAction,
} from "../../../lib/TrackingAdapter";
import { CTARowDivider } from "../../CTARowDivider/CTARowDivider";
import { getTierDisplayName } from "../subscriptionUtils";
import { getWarningFeatures } from "./WarningContent";

import style from "./CancellationWarningModal.module.css";

type CancellationWarningModalProps = {
  open: boolean;
  isLoading: boolean;
  subscriptionTier: SubscriptionTier;
  subscriptionExpirationDate: string;
  onClose: () => void;
  onCTA: () => void;
};

export const CancellationWarningModal: React.FC<CancellationWarningModalProps> = ({
  open,
  isLoading,
  subscriptionTier,
  subscriptionExpirationDate,
  onClose,
  onCTA,
}) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const tierDisplayName = getTierDisplayName(subscriptionTier);
  const isStudioPricing =
    subscriptionTier === SubscriptionTier.Studio ||
    subscriptionTier === SubscriptionTier.Individual;
  const downgradeTierName = isStudioPricing ? "Free" : "Basic";
  const features = getWarningFeatures(subscriptionTier, t);

  return (
    <Modal
      open={open}
      onOpen={() => tracker.trackModalShown(ModalShownAction.CANCEL_SUBSCRIPTION)}
      size="medium"
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.CANCEL_SUBSCRIPTION);
        onClose();
      }}
    >
      <div className={style.modalContainer}>
        <Icon_ExclamationFilled16 className={style.warningIcon} />
        <div>
          <div className={style.title}>{t(m.TeamBilling.AreYouSureCancel)}</div>
          <div>
            {t(m.CancellationWarningModal.Body, {
              downgradeTierName,
              tierDisplayName,
              date: subscriptionExpirationDate,
              styledDate: (chunks: ReactNode) => (
                <span key="date" className={style.expirationDate}>
                  {chunks}
                </span>
              ),
            })}
          </div>
        </div>

        <InfoBar
          variant="critical"
          title={t(m.CancellationWarningModal.LimitsYourWork)}
          text={
            <ul className={style.featureList}>
              {features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          }
          className={style.featureListContainer}
        />

        <CTARowDivider className={style.divider} />

        <div className={style.modalButtonContainer}>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CANCEL,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
              onClose();
            }}
            disabled={isLoading}
            variant="primary"
          >
            {t(m.TeamBilling.KeepTier, { tierName: tierDisplayName })}
          </Button>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CONTINUE,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
              onCTA();
            }}
            disabled={isLoading}
            variant="borderless"
          >
            {t(m.TeamBilling.CancelSubscription)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
